import { withViewAs } from '../ui/view-as-links.js';

// CONCEPT: the one part of YMM clearing (#866) that acts on its own - an open, idle storefront
// tab that takes itself back to the homepage once the shopper's selected vehicle has expired.
// A counter terminal left sitting on a 2014 Silverado is the whole reason the feature exists,
// and a tab nobody touches never issues the request that would otherwise notice.
//
// USE: initVehicleIdleTimer() once from main.js on DOM ready. It gates itself and returns
// whether it armed; stopVehicleIdleTimer() undoes it.
//
// THE SERVER OWNS CLEARING. Nothing here removes a vehicle, a facet cookie or a session key.
// The tab's entire job is to ask a question and, if the answer is yes, to navigate to the route
// that clears (#872) - so there is exactly one clearing implementation, and it is the one that
// can also expire the cookie and file the activity row.
//
// ASK, THEN ACT. The last-activity timestamp lives in the SESSION, which is shared by every tab
// in the browser. So a tab that has sat still for twenty minutes may be sitting next to one the
// shopper is working in, and a timer that trusted its own clock would yank the vehicle out from
// under them. Every fire therefore polls the idle-check endpoint first and stands down on
// `expired: false`.
//
// ASKING IS NOT ENOUGH ON ITS OWN, which is the second review's P1 (PR #877). The server only
// knows about REQUESTS. A shopper reading, scrolling and typing in the foreground tab issues
// none of them, so the session stamp goes stale while a person is plainly sitting there - and
// the background tab, idle by its own clock, is truthfully told `expired: true` and navigates,
// clearing the vehicle out from under an active shopper mid-sentence.
//
// So DOM activity is shared between tabs too, through a throttled localStorage heartbeat, and a
// tab stands down when ANY tab has seen interaction inside the timeout. localStorage is
// same-origin and shared by every tab in the profile, which is precisely the set of tabs that
// also shares the session cookie: two browsers or two profiles never share a heartbeat, but they
// never shared a session either, so there is nothing there to coordinate. Every read and write
// is wrapped - localStorage THROWS rather than answering null in some privacy modes - and an
// unavailable or unusable heartbeat falls back to the ask-then-act behaviour on its own, which
// still clears. Failing the other way would let a privacy setting switch the feature off.

// The hidden input layout.inc emits, and the gate. See initVehicleIdleTimer().
const TIMEOUT_INPUT_ID = 'ymm-idle-timeout-minutes';

const IDLE_CHECK_URL = '/catalog/vehicle-idle-check';
const TIMEOUT_RESET_URL = '/catalog/reset-vehicle/timeout';

// How the poll cadence is derived from the site's configured timeout, rather than being a fixed
// number of seconds. A site on a twelve-hour timeout must not generate 144 times the requests of
// a site on five minutes for the same amount of idleness (#866, story 34). A quarter also bounds
// how late a fire can be: the tab notices staleness within timeout + timeout/4.
const POLL_DIVISOR = 4;

// Floor for the derived interval. The Control Panel clamps the setting to a floor of 5 minutes
// and a ceiling of half the session lifetime, so in practice the divisor never produces anything
// under 75 seconds - but the setting is a row in a table shared with a live PACE-1 site, and a
// hand-written 1 must not turn into a 15-second poll.
const MIN_POLL_MS = 15000;

// Interaction is throttled by comparing timestamps rather than by scheduling anything: mousemove
// fires hundreds of times a second and the cheapest possible handler is one that returns. The
// consequence worth knowing is that activity inside the window is DISCARDED, not deferred - the
// deadline advances to the moment of the first event in a burst, not the last.
const ACTIVITY_THROTTLE_MS = 1000;

// Where the cross-tab activity heartbeat lives. One key, one epoch-milliseconds value, written
// by whichever tab last saw interaction and read by every tab before it acts. Namespaced like a
// cookie name rather than bare ('last-activity' on a shared origin is asking for a collision),
// and deliberately NOT per-site-hash: localStorage is already partitioned by origin, and a
// distributor site is an origin.
const ACTIVITY_STORAGE_KEY = 'pace-ymm-last-activity';

// mousemove/keydown/scroll/click - real interaction, and nothing that fires on its own. Bound
// under one jQuery namespace so stopVehicleIdleTimer() can drop the whole set in one call.
const ACTIVITY_EVENTS = 'mousemove.vehicleIdle keydown.vehicleIdle scroll.vehicleIdle click.vehicleIdle';

let pollTimer = null;
let lastActivity = 0;
let checkInFlight = false;

/**
 * Navigation, behind one indirection because jsdom makes window.location.assign
 * non-configurable - it cannot be spied on, and replacing window.location wholesale to observe
 * one redirect is a heavier fake than the thing it tests. Same shape as favoritesNav in
 * src/js/favorites/index.js, and called by name so a test's spy is the function that runs.
 */
export const vehicleIdleNav = {
    go(url) {
        window.location.assign(url);
    },
};

/**
 * The configured timeout in minutes, or 0 for "this tab does not arm".
 *
 * The input is ABSENT rather than zero on every page that must not run this: the control panel
 * and dev tools layouts, and any storefront page with no vehicle selected. Absence and a
 * non-numeric value collapse to the same answer so a hand-edited setting cannot start clearing
 * vehicles on some arbitrary schedule.
 */
function configuredTimeoutMinutes() {
    const $input = $('#' + TIMEOUT_INPUT_ID);

    if (!$input.length) {
        return 0;
    }

    const minutes = parseInt($input.val(), 10);

    return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
}

/**
 * Publish this tab's interaction where its siblings can see it.
 *
 * Wrapped because localStorage does not merely come back empty when it is unavailable: a browser
 * set to block site data throws on the property itself, and a private window that allows the
 * property can still throw a quota error on the write. This runs from a mousemove handler, where
 * an unguarded throw would fire hundreds of times a second, and a tab that cannot write simply
 * contributes nothing to the heartbeat. Optional catch binding, since the build targets es2020.
 */
function publishSharedActivity(now) {
    try {
        window.localStorage.setItem(ACTIVITY_STORAGE_KEY, String(now));
    } catch {
        // No heartbeat for this browser. sharedActivityWithin() degrades to the same answer.
    }
}

/**
 * Has ANY tab on this origin seen real interaction within the last timeoutMs?
 *
 * Every unusable read - localStorage throwing, the key absent, a non-numeric value, a stamp in
 * the FUTURE from a clock that moved backwards - answers false, and false is the answer that
 * lets this tab act. That direction is chosen deliberately: the fallback is the ask-then-act
 * behaviour that shipped, where the server has already been asked and has already declined for
 * any session another tab refreshed with a real request. Answering true on a bad read would mean
 * a browser with localStorage disabled never clears a vehicle at all, which is the shared-counter
 * feature switched off by a privacy setting with nothing to show for it.
 */
function sharedActivityWithin(timeoutMs) {
    let stored;

    try {
        stored = window.localStorage.getItem(ACTIVITY_STORAGE_KEY);
    } catch {
        return false;
    }

    const stamped = parseInt(stored, 10);

    if (!Number.isFinite(stamped)) {
        return false;
    }

    const age = Date.now() - stamped;

    return age >= 0 && age < timeoutMs;
}

function markActive() {
    const now = Date.now();

    if (now - lastActivity < ACTIVITY_THROTTLE_MS) {
        return;
    }

    lastActivity = now;

    // Inside the throttle, so this is one write per tab per second rather than one per mousemove.
    // The consequence worth knowing is the same one the local stamp has: activity inside the
    // window is discarded rather than deferred, so the shared stamp can trail real interaction by
    // up to a second - immaterial against a timeout measured in minutes.
    publishSharedActivity(now);
}

/**
 * One tick. Costs nothing while the shopper is present: the elapsed-time check is local, so a
 * tab in use makes no requests at all and only an idle one reaches the network.
 */
function checkIfIdle(timeoutMs) {
    if (checkInFlight) {
        return;
    }

    if (Date.now() - lastActivity < timeoutMs) {
        return;
    }

    checkInFlight = true;

    $.ajax({
        // withViewAs() rather than the bare path: AAM staff browsing as a customer (?au=) must
        // see the timeout behave the way that customer's would. The acting user rides in the
        // query string or not at all - user_helper.php reads $_GET['au'] with no session
        // fallback - and it is what earns the activity row the right author on the way through
        // reset_vehicle(). applyViewAsToLinks() cannot help here: these urls are not in the DOM.
        url: withViewAs(IDLE_CHECK_URL),
        type: 'GET',
        // PARSE the payload. setJSON() pretty-prints outside production, so the raw bytes carry
        // newlines and indentation and anything matching on the body text would be reading
        // whitespace. dataType pins the parse rather than leaving it to content sniffing.
        dataType: 'json',
        cache: false,
        success: function (response) {
            checkInFlight = false;

            if (!response || response.expired !== true) {
                // STAND DOWN. The session is not idle, which means some other tab is being used,
                // and this tab has no business acting. Treating the answer as activity is what
                // keeps a background tab from re-asking every tick for as long as the shopper
                // works in the foreground one: it costs at most a further timeout of staleness
                // in a tab nobody is looking at, and the server clears on the next real request
                // from any tab regardless.
                lastActivity = Date.now();

                return;
            }

            // The server says this SESSION is idle, and it is right about requests. It cannot see
            // the shopper reading and typing in the foreground tab, which issues none - so a tab
            // that acted on this answer alone would clear a vehicle that is in active use. Any
            // tab's interaction inside the timeout stands this one down, and it re-asks a full
            // timeout later; the server clears on the next real request from any tab regardless.
            if (sharedActivityWithin(timeoutMs)) {
                lastActivity = Date.now();

                return;
            }

            // Unbind before navigating so a mousemove landing during the unload cannot restart
            // anything, and so a second answer cannot navigate twice.
            stopVehicleIdleTimer();
            vehicleIdleNav.go(withViewAs(TIMEOUT_RESET_URL));
        },
        error: function () {
            // A failed poll is not evidence of anything. Release the latch and ask again next
            // tick rather than navigating on a network blip or a 500.
            checkInFlight = false;
        },
    });
}

/**
 * Undo everything initVehicleIdleTimer() set up. Called by init itself, so arming twice is
 * arming once, and by the success path before it navigates.
 */
export function stopVehicleIdleTimer() {
    if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
    }

    $(document).off('.vehicleIdle');
    checkInFlight = false;
}

/**
 * Arm the idle timer, if this page is one that should have it.
 *
 * THE GATE IS MANDATORY, NOT DEFENSIVE. main.js is the bundle for all THREE layouts -
 * layout.inc, layout_c.inc and layout_d.inc - so without it this runs in the Control Panel and
 * in dev tools, where twenty minutes spent administering a site would read as shopper
 * inactivity and clear the vehicle out of the administrator's own storefront session (#866,
 * story 22). Only layout.inc emits #ymm-idle-timeout-minutes, and only while a vehicle is
 * actually selected and the site has set a timeout, which makes the input's presence the whole
 * gate: no vehicle costs nothing, feature off costs nothing, wrong layout costs nothing.
 *
 * @returns {boolean} whether the timer armed
 */
export function initVehicleIdleTimer() {
    stopVehicleIdleTimer();

    const minutes = configuredTimeoutMinutes();

    if (minutes < 1) {
        return false;
    }

    const timeoutMs = minutes * 60 * 1000;

    lastActivity = Date.now();
    // A page that just rendered with a vehicle on it IS activity - it was a storefront request,
    // and the server stamped its own clock for it - so arming publishes too. Without this a tab
    // opened and left alone would contribute nothing to the heartbeat until its first mousemove.
    publishSharedActivity(lastActivity);
    $(document).on(ACTIVITY_EVENTS, markActive);
    pollTimer = setInterval(
        function () {
            checkIfIdle(timeoutMs);
        },
        Math.max(Math.round(timeoutMs / POLL_DIVISOR), MIN_POLL_MS)
    );

    return true;
}
